import React from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../lib/convex';
import { Loader2 } from 'lucide-react';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const { isLoaded, isSignedIn, user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const preferences = useQuery(api.posts.getUserPreferences, isSignedIn ? undefined : "skip");

  // Get user service tier - default to free if not found
  const userTier = preferences?.serviceTier || user?.privateMetadata?.serviceTier || 'free';
  const isPremium = userTier !== 'free';

  // Public pages anyone can access
  const publicUrlPatterns = [
    '/$', // Exact home page
    '/blog',
    '/privacy',
    '/terms',
    '/contact',
    '/thank-you',
    '/cookie-settings',
    '/login',
    '/upgrade',
    '/why',
    '/docs'
  ];

  // Pages that require authentication (premium or free)
  const authRequiredPatterns = [
    '/writer',
    '/posts',
    '/admin'
  ];
  
  // Check if current path is a public URL
  const isPublicUrl = publicUrlPatterns.some(pattern => {
    if (pattern.endsWith('$')) {
      // Handle exact match with $ pattern
      return location.pathname === pattern.slice(0, -1);
    }
    return location.pathname.startsWith(pattern);
  });

  // Check if current path requires authentication
  const requiresAuth = authRequiredPatterns.some(pattern => 
    location.pathname.startsWith(pattern)
  );

  React.useEffect(() => {
    if (isLoaded) {
      // If path requires authentication but user is not signed in, redirect to login
      if (requiresAuth && !isSignedIn) {
        navigate('/login', { replace: true });
        return;
      }

      // If path requires authentication, user is signed in but not premium, redirect to upgrade
      if (requiresAuth && isSignedIn && !isPremium) {
        navigate('/upgrade', { replace: true });
        return;
      }
    }
  }, [isLoaded, isSignedIn, isPremium, requiresAuth, navigate, location.pathname]);

  // Show loading state while checking auth
  if (!isLoaded && requiresAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  // Always render children for public URLs or if user meets auth requirements
  return <>{children}</>;
}